import { useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { X, User, KanbanSquare, CalendarDays, LogOut, BookOpen, type LucideIcon } from 'lucide-react'
import { usePermissions } from '../../hooks/usePermissions'
import { useAuthStore } from '../../store/authStore'

interface MoreSheetProps {
  isOpen: boolean
  onClose: () => void
}

interface SheetItem {
  name: string
  href?: string
  icon: LucideIcon
  module?: string
  action?: () => void
  destructive?: boolean
}

export default function MoreSheet({ isOpen, onClose }: MoreSheetProps) {
  const { canAccessPage } = usePermissions()
  const { logout } = useAuthStore()

  useEffect(() => {
    if (!isOpen) return
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const items: SheetItem[] = [
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Projects', href: '/projects', icon: KanbanSquare, module: 'pwa_project_monitoring' },
    { name: 'Leave', href: '/leave', icon: CalendarDays, module: 'pwa_leave' },
    { name: 'Learning', href: '/learning', icon: BookOpen, module: 'pwa_learning' },
  ]
  const visible = items.filter((i) => !i.module || canAccessPage(i.module))

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative w-full rounded-t-2xl bg-white pb-safe shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3">
          <h2 className="text-base font-semibold">More</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <ul className="divide-y divide-gray-100">
          {visible.map((i) => (
            <li key={i.name}>
              <NavLink
                to={i.href || '#'}
                onClick={onClose}
                className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50"
              >
                <i.icon className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-800">{i.name}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <button
              onClick={() => { logout(); onClose() }}
              className="flex w-full items-center gap-3 px-5 py-3 hover:bg-red-50"
            >
              <LogOut className="h-5 w-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">Logout</span>
            </button>
          </li>
        </ul>
        <div className="h-6" />
      </div>
    </div>
  )
}
